import { countryName, onLocaleChange, t } from '../i18n/index.js';
import { flagEmoji } from '../domain/flagEmoji.js';
import { buildAppComparisonHref } from '../domain/appComparisonLink.js';
import { buildDailyOpportunityReport } from '../domain/opportunityDailyReport.js';
import { scoreOpportunityCandidate } from '../domain/opportunityRadar.js';

const DEFAULT_PARAMS = Object.freeze({
  market: 'us',
  category: 'all',
  chartType: 'top-free',
  limit: 50,
});

const CHART_OPPORTUNITY_ENDPOINT = '/opportunities/v1/latest.json';

let lastRender = null;

export function chartOpportunityEndpoint() {
  return CHART_OPPORTUNITY_ENDPOINT;
}

export function opportunityEndpoint(params = {}) {
  return apiEndpoint('/api/opportunities', params);
}

export function scanEndpoint(params = {}) {
  return apiEndpoint('/api/scan', params);
}

function apiEndpoint(path, params = {}) {
  const merged = { ...DEFAULT_PARAMS, ...params };
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(merged)) {
    if (value !== null && value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  return `${path}?${search.toString()}`;
}

export async function loadOpportunityRadar(container, options = {}) {
  if (!container) return;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const params = { ...DEFAULT_PARAMS, ...(options.params ?? {}) };

  renderOpportunityRadar(container, { kind: 'loading', params }, options);

  if (typeof fetchImpl !== 'function') {
    renderOpportunityRadar(container, {
      kind: 'paused',
      status: 'budget_exhausted',
      reason: 'fetch_unavailable',
      params,
      opportunities: [],
    }, options);
    return;
  }

  let response;
  let body;
  try {
    response = await fetchImpl(chartOpportunityEndpoint(), {
      headers: { Accept: 'application/json' },
    });
    if (response.status !== 404) {
      body = await parseOpportunityJson(response);
      renderOpportunityRadar(container, stateFromChartArtifact(body, params), options);
      return;
    }

    response = await fetchImpl(opportunityEndpoint(params), {
      headers: { Accept: 'application/json' },
    });
    body = await parseOpportunityJson(response);
  } catch (err) {
    renderOpportunityRadar(container, {
      kind: 'error',
      message: err instanceof Error ? err.message : String(err),
      params,
      opportunities: [],
    }, options);
    return;
  }

  renderOpportunityRadar(container, stateFromResponse(body, response.status), options);
}

export async function scanOpportunityRadar(container, options = {}) {
  if (!container) return;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const previousState = options.previousState ?? lastRender?.state ?? {};
  const params = { ...DEFAULT_PARAMS, ...(previousState.params ?? {}), ...(options.params ?? {}) };
  const previousOpportunities = Array.isArray(previousState.opportunities) ? previousState.opportunities : [];
  const previousChartCandidates = Array.isArray(previousState.chartCandidates) ? previousState.chartCandidates : [];

  renderOpportunityRadar(container, {
    ...previousState,
    kind: 'refreshing',
    status: 'refreshing',
    params,
    opportunities: previousOpportunities,
    chartCandidates: previousChartCandidates,
  }, options);

  if (typeof fetchImpl !== 'function') {
    renderOpportunityRadar(container, keepPreviousOpportunities({
      kind: 'paused',
      status: 'budget_exhausted',
      reason: 'fetch_unavailable',
      params,
      opportunities: [],
    }, previousState), options);
    return;
  }

  let response;
  let body;
  try {
    response = await fetchImpl(scanEndpoint(params), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    body = await parseOpportunityJson(response);
  } catch (err) {
    renderOpportunityRadar(container, keepPreviousOpportunities({
      kind: 'error',
      message: err instanceof Error ? err.message : String(err),
      params,
      opportunities: [],
    }, previousState), options);
    return;
  }

  renderOpportunityRadar(container, keepPreviousOpportunities(
    stateFromResponse(body, response.status),
    previousState,
  ), options);
}

async function parseOpportunityJson(response) {
  try {
    return await response.json();
  } catch (err) {
    if (response?.status === 404) {
      return { status: 'skipped', reason: 'cache_miss', opportunities: [] };
    }
    throw err;
  }
}

export function stateFromResponse(body = {}, httpStatus = 200) {
  const opportunities = Array.isArray(body.opportunities) ? body.opportunities : [];
  const params = body.params ?? DEFAULT_PARAMS;
  const base = {
    status: body.status ?? null,
    reason: body.reason ?? null,
    retryAfter: body.retryAfter ?? null,
    nextAllowedAt: body.nextAllowedAt ?? null,
    generatedAt: body.generatedAt ?? null,
    report: body.report ?? null,
    params,
    opportunities,
  };

  if (opportunities.length > 0) {
    const reason = body.reason ?? null;
    if (body.status === 'rate_limited' || reason === 'rate_limited' || reason === 'scan_intent_budget_exhausted' || reason === 'queue_full') {
      return { ...base, kind: 'limited', status: body.status ?? 'rate_limited' };
    }
    if (body.status === 'budget_exhausted' || reason === 'budget_exhausted' || reason === 'storage_error' || reason === 'storage_unavailable') {
      return { ...base, kind: 'paused', status: body.status ?? 'budget_exhausted' };
    }
    return {
      ...base,
      kind: body.status === 'rate_limited' ? 'limited' : 'ready',
      status: body.status ?? 'cached',
    };
  }

  if (body.status === 'queued') {
    return { ...base, kind: 'queued' };
  }
  if (body.status === 'skipped' && (body.reason === 'cooldown' || body.reason === 'deduped')) {
    return { ...base, kind: 'limited' };
  }
  if (body.status === 'rate_limited' || httpStatus === 429) {
    return { ...base, ...body, kind: 'limited', params, opportunities };
  }
  if (body.status === 'budget_exhausted' || httpStatus === 503) {
    return { ...base, ...body, kind: 'paused', params, opportunities };
  }
  if (body.status === 'skipped' || httpStatus === 404) {
    return { ...base, ...body, kind: 'empty', params, opportunities };
  }
  if (httpStatus >= 400) {
    return {
      ...base,
      kind: 'error',
      message: body.error || body.reason || `HTTP ${httpStatus}`,
    };
  }
  return { ...base, ...body, kind: 'empty', params, opportunities };
}

export function stateFromChartArtifact(body = {}, params = DEFAULT_PARAMS) {
  const chartCandidates = normalizeChartCandidates(body);
  const base = {
    kind: chartCandidates.length > 0 ? 'ready' : 'empty',
    status: 'cached',
    reason: null,
    retryAfter: null,
    nextAllowedAt: null,
    generatedAt: body?.capturedAt ?? null,
    report: null,
    params,
    opportunities: [],
    chartArtifact: body && typeof body === 'object' ? body : null,
    chartCandidates,
    baselineStatus: typeof body?.baselineStatus === 'string' ? body.baselineStatus : null,
    baselineDate: body?.baselineDate ?? null,
    methodology: typeof body?.methodology === 'string' ? body.methodology : null,
  };
  return base;
}

export function renderOpportunityRadar(container, state, options = {}) {
  if (!container) return;
  container.hidden = false;
  lastRender = { container, state, options };

  const controls = renderControls(state, options);
  const status = renderStatusBlock(state, options);
  const breakoutSections = renderChartBreakoutSections(state);
  const dailyReport = renderDailyReport(state);
  const cards = (state.opportunities ?? [])
    .map(normalizeOpportunity)
    .filter((item) => item.app)
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
    .map(renderOpportunityCard)
    .join('');

  container.innerHTML = `
    <div class="opportunity-radar">
      <header class="opportunity-radar__header">
        <div>
          <p class="eyebrow">${escapeHtml(t('opportunityRadar.eyebrow'))}</p>
          <h1>${accentTitleSegment(t('opportunityRadar.title'), 'app opportunities')}</h1>
          <p>${escapeHtml(t('opportunityRadar.subtitle'))}</p>
        </div>
        ${controls}
      </header>
      ${status}
      ${breakoutSections}
      ${dailyReport}
      ${cards ? `<div class="opportunity-radar__grid">${cards}</div>` : ''}
    </div>
  `.trim();

  const retry = container.querySelector('[data-opportunity-retry]');
  if (retry && typeof options.onRetry === 'function') {
    retry.addEventListener('click', options.onRetry);
  }
  const scan = container.querySelector('[data-opportunity-scan]');
  if (scan && typeof options.onScan === 'function' && !scan.disabled) {
    scan.addEventListener('click', options.onScan);
  }
}

function normalizeChartCandidates(body = {}) {
  const candidates = Array.isArray(body?.candidates) ? body.candidates : [];
  return candidates
    .map((candidate) => {
      const trackId = candidate?.trackId ?? null;
      if (trackId === null || trackId === '') return null;
      return {
        trackId,
        name: candidate?.name ?? null,
        market: candidate?.market ?? null,
        chartType: candidate?.chartType ?? null,
        currentRank: integerOrNull(candidate?.currentRank),
        previousRank: integerOrNull(candidate?.previousRank),
        rankGain: integerOrNull(candidate?.rankGain),
        kind: typeof candidate?.kind === 'string' ? candidate.kind : 'new-entry',
        capturedAt: candidate?.capturedAt ?? body?.capturedAt ?? null,
        baselineDate: candidate?.baselineDate ?? body?.baselineDate ?? null,
        baselineStatus: candidate?.baselineStatus ?? body?.baselineStatus ?? null,
        methodology: candidate?.methodology ?? body?.methodology ?? null,
        reviewHints: Array.isArray(candidate?.reviewHints) ? candidate.reviewHints : [],
        filterStatus: filterStatus(candidate?.filterStatus),
        filterReasons: Array.isArray(candidate?.filterReasons)
          ? candidate.filterReasons.filter((reason) => typeof reason === 'string' && reason)
          : [],
        filterCategory: typeof candidate?.filterCategory === 'string' ? candidate.filterCategory : null,
      };
    })
    .filter(Boolean);
}

function renderChartBreakoutSections(state) {
  const candidates = Array.isArray(state.chartCandidates) ? state.chartCandidates : [];
  const artifact = state.chartArtifact ?? {};
  const baselineStatus = state.baselineStatus ?? artifact.baselineStatus ?? null;
  const baselineNotice = renderBaselineNotice(baselineStatus, artifact.baselineDate ?? state.baselineDate ?? null, artifact.methodology ?? state.methodology ?? null);

  if (candidates.length === 0) {
    if (!state.chartArtifact) return '';
    return `
      <section class="chart-breakouts" aria-label="${escapeAttr(t('opportunityRadar.breakoutsAria'))}">
        ${baselineNotice}
        <div class="chart-breakouts__empty">${escapeHtml(t('opportunityRadar.breakoutsEmpty'))}</div>
      </section>
    `;
  }

  const reviewCandidates = candidates.filter((candidate) => candidate.filterStatus !== 'candidate');
  const unflaggedCandidates = candidates.filter((candidate) => candidate.filterStatus === 'candidate');
  const needsBaseline = unflaggedCandidates.filter((candidate) => candidate.baselineStatus !== 'complete');
  const completeCandidates = unflaggedCandidates.filter((candidate) => candidate.baselineStatus === 'complete');
  const sections = [
    {
      key: 'hot',
      title: t('opportunityRadar.section.hotNewEntries'),
      candidates: completeCandidates.filter((candidate) => candidate.kind === 'hot-new-entry' || candidate.kind === 'new-entry'),
    },
    {
      key: 'fast',
      title: t('opportunityRadar.section.fastClimbers'),
      candidates: completeCandidates.filter((candidate) => candidate.kind === 'fast-climber'),
    },
    {
      key: 'steady',
      title: t('opportunityRadar.section.steadyClimbers'),
      candidates: completeCandidates.filter((candidate) => candidate.kind === 'steady-climber' || candidate.kind === 'multi-window-climber'),
    },
    {
      key: 'baseline',
      title: t('opportunityRadar.section.needsBaseline'),
      candidates: needsBaseline,
    },
    {
      key: 'review',
      title: t('opportunityRadar.section.reviewFlags'),
      candidates: reviewCandidates,
    },
  ].filter((section) => section.candidates.length > 0);

  return `
    <section class="chart-breakouts" aria-label="${escapeAttr(t('opportunityRadar.breakoutsAria'))}">
      <div class="chart-breakouts__header">
        <div>
          <p class="eyebrow">${escapeHtml(t('opportunityRadar.breakoutsEyebrow'))}</p>
          <h2>${escapeHtml(t('opportunityRadar.breakoutsTitle'))}</h2>
        </div>
        <span class="chart-breakouts__stamp">${escapeHtml(t('opportunityRadar.capturedAt', { date: artifact.capturedAt ?? state.generatedAt ?? 'unknown' }))}</span>
      </div>
      ${baselineNotice}
      ${sections.map(renderChartBreakoutSection).join('')}
    </section>
  `;
}

function renderChartBreakoutSection(section) {
  return `
    <section class="chart-breakouts__section" aria-label="${escapeAttr(section.title)}">
      <h3>${escapeHtml(section.title)}</h3>
      <div class="chart-breakouts__grid">
        ${section.candidates.map(renderChartBreakoutCard).join('')}
      </div>
    </section>
  `;
}

function renderChartBreakoutCard(candidate) {
  const market = String(candidate.market ?? '').toLowerCase();
  const marketLabel = market ? `${flagEmoji(market)} ${countryName(market, market.toUpperCase())}` : t('opportunityRadar.unknownMarket');
  const href = buildAppComparisonHref(candidate.trackId, market);
  const reviewHint = reviewHintText(candidate);
  const statusClass = candidate.filterStatus === 'candidate' ? '' : ` chart-breakout-card--${candidate.filterStatus.replace('_', '-')}`;
  const filterRows = renderFilterRows(candidate);

  return `
    <a class="chart-breakout-card${statusClass}" href="${escapeAttr(href)}">
      <div class="chart-breakout-card__badges">
        <span class="chart-breakout-card__kind">${escapeHtml(kindLabel(candidate.kind))}</span>
        ${candidate.filterStatus === 'candidate' ? '' : `<span class="chart-breakout-card__filter">${escapeHtml(filterStatusLabel(candidate.filterStatus))}</span>`}
      </div>
      <h4>${escapeHtml(candidate.name ?? t('opportunityRadar.unnamedApp'))}</h4>
      <dl>
        <div>
          <dt>${escapeHtml(t('opportunityRadar.field.market'))}</dt>
          <dd>${escapeHtml(marketLabel)}</dd>
        </div>
        <div>
          <dt>${escapeHtml(t('opportunityRadar.field.chartType'))}</dt>
          <dd>${escapeHtml(chartTypeLabel(candidate.chartType))}</dd>
        </div>
        <div>
          <dt>${escapeHtml(t('opportunityRadar.field.rankMovement'))}</dt>
          <dd>${escapeHtml(rankMovementText(candidate))}</dd>
        </div>
        <div>
          <dt>${escapeHtml(t('opportunityRadar.field.capturedDate'))}</dt>
          <dd>${escapeHtml(candidate.capturedAt ?? 'unknown')}</dd>
        </div>
        <div>
          <dt>${escapeHtml(t('opportunityRadar.field.reviewHint'))}</dt>
          <dd>${escapeHtml(reviewHint)}</dd>
        </div>
        ${filterRows}
      </dl>
    </a>
  `;
}

function renderFilterRows(candidate) {
  if (candidate.filterStatus === 'candidate') return '';
  const category = candidate.filterCategory ? filterCategoryLabel(candidate.filterCategory) : '';
  const reasons = candidate.filterReasons.length
    ? candidate.filterReasons.map(filterReasonText).join('; ')
    : t('opportunityRadar.filterReason.none');
  return `
    <div>
      <dt>${escapeHtml(t('opportunityRadar.field.filterStatus'))}</dt>
      <dd>${escapeHtml(category ? `${filterStatusLabel(candidate.filterStatus)} · ${category}` : filterStatusLabel(candidate.filterStatus))}</dd>
    </div>
    <div>
      <dt>${escapeHtml(t('opportunityRadar.field.filterReasons'))}</dt>
      <dd>${escapeHtml(reasons)}</dd>
    </div>
  `;
}

function renderBaselineNotice(status, baselineDate, methodology) {
  if (!status) return '';
  const tone = status === 'complete' ? '' : ' chart-breakouts__baseline--warn';
  const label = t(`opportunityRadar.baseline.${status}`);
  const dateText = baselineDate
    ? t('opportunityRadar.baselineDate', { date: baselineDate })
    : t('opportunityRadar.baselineMissingDate');
  const partialMessage = status === 'partial'
    ? `<strong>${escapeHtml(t('opportunityRadar.partialBaselineWarning'))}</strong>`
    : '';
  const insufficientMessage = status === 'insufficient'
    ? `<strong>${escapeHtml(t('opportunityRadar.insufficientBaselineWarning'))}</strong>`
    : '';
  const localizedMethodology = baselineMethodologyText(status) || methodology;
  return `
    <div class="chart-breakouts__baseline${tone}">
      <span>${escapeHtml(label)}</span>
      <span>${escapeHtml(dateText)}</span>
      ${partialMessage}
      ${insufficientMessage}
      ${localizedMethodology ? `<p>${escapeHtml(localizedMethodology)}</p>` : ''}
    </div>
  `;
}

function baselineMethodologyText(status) {
  if (!['complete', 'partial', 'insufficient'].includes(status)) return '';
  return t(`opportunityRadar.methodology.${status}`);
}

export function bindOpportunityRadarLocaleRefresh() {
  onLocaleChange(() => {
    if (lastRender?.container && !lastRender.container.hidden) {
      renderOpportunityRadar(lastRender.container, lastRender.state, lastRender.options);
    }
  });
}

function normalizeOpportunity(item) {
  if (item?.signals) {
    return scoreOpportunityCandidate(item);
  }
  return {
    app: item?.app ?? null,
    score: finiteOrNull(item?.score),
    targetScores: Array.isArray(item?.targetScores) ? item.targetScores : [],
    sourceSummary: item?.sourceSummary ?? {},
    topReasons: Array.isArray(item?.topReasons) ? item.topReasons : [],
  };
}

function renderOpportunityCard(item) {
  const score = finiteOrNull(item.score);
  const factors = aggregateFactors(item);
  const reasons = (item.topReasons && item.topReasons.length ? item.topReasons : item.targetScores.flatMap((target) => target.reasons ?? []))
    .slice(0, 3)
    .map((reason) => `<li>${escapeHtml(reasonText(reason))}</li>`)
    .join('');

  return `
    <article class="opportunity-card">
      <div class="opportunity-card__topline">
        <div>
          <h2>${escapeHtml(item.app.name ?? item.app.trackName ?? t('opportunityRadar.unnamedApp'))}</h2>
          <p>${escapeHtml(item.app.genreName ?? t('opportunityRadar.unknownGenre'))}</p>
        </div>
        <div class="opportunity-card__score" aria-label="${escapeAttr(t('opportunityRadar.scoreAria', { score: score ?? '—' }))}">
          <span>${score ?? '—'}</span>
          <small>${escapeHtml(t('opportunityRadar.scoreLabel'))}</small>
        </div>
      </div>
      <dl class="opportunity-card__factors">
        ${renderFactor('sourceHeat', factors.sourceHeat)}
        ${renderFactor('targetCold', factors.targetCold)}
        ${renderFactor('localizationGap', factors.localizationGap)}
        ${renderFactor('substituteWeakness', factors.substituteWeakness)}
      </dl>
      <div class="opportunity-card__reasons">
        <h3>${escapeHtml(t('opportunityRadar.why'))}</h3>
        <ul>${reasons || `<li>${escapeHtml(t('opportunityRadar.noReasons'))}</li>`}</ul>
      </div>
    </article>
  `;
}

function renderDailyReport(state) {
  const opportunities = Array.isArray(state.opportunities) ? state.opportunities : [];
  if (opportunities.length === 0) return '';

  const report = state.report && Array.isArray(state.report.candidates)
    ? state.report
    : buildDailyOpportunityReport({
      generatedAt: state.generatedAt ?? null,
      opportunities,
    });
  const candidates = Array.isArray(report.candidates) ? report.candidates : [];
  const rows = candidates
    .map((candidate) => `
      <li>
        <div class="opportunity-report__candidate-head">
          <span>${escapeHtml(String(candidate.rank ?? ''))}</span>
          <strong>${escapeHtml(candidate.appName ?? t('opportunityRadar.unnamedApp'))}</strong>
          <em>${escapeHtml(candidate.score == null ? '无分' : `${candidate.score}`)}</em>
        </div>
        <p>${escapeHtml(candidate.sourceHeat ?? '')}</p>
        <p>${escapeHtml(candidate.localizationGap ?? '')}</p>
        <p>${escapeHtml(candidate.substituteWeakness ?? '')}</p>
        <p>${escapeHtml(candidate.recommendation ?? '')}</p>
      </li>
    `)
    .join('');
  const statusSample = report.statusSample
    ? `
      <div class="opportunity-report__status">
        <strong>Status 样例验证</strong>
        <p>${escapeHtml(report.statusSample.verdict)}</p>
      </div>
    `
    : '';

  return `
    <section class="opportunity-report" aria-label="每日机会报告">
      <div class="opportunity-report__header">
        <div>
          <p class="eyebrow">${escapeHtml(t('opportunityRadar.dailyReportEyebrow'))}</p>
          <h2>${escapeHtml(report.headline ?? '每日机会报告')}</h2>
        </div>
        <span class="opportunity-report__badge">${escapeHtml(report.status === 'ready' ? '可复核' : '信号不足')}</span>
      </div>
      <p class="opportunity-report__summary">${escapeHtml(report.summary ?? '')}</p>
      ${rows ? `<ol class="opportunity-report__list">${rows}</ol>` : ''}
      ${statusSample}
      <p class="opportunity-report__method">${escapeHtml(report.methodology ?? '')}</p>
    </section>
  `;
}

function renderFactor(key, value) {
  const percent = value === null ? null : Math.round(value * 100);
  const width = percent === null ? 0 : percent;
  const label = t(`opportunityRadar.factor.${key}`);
  return `
    <div class="opportunity-card__factor">
      <dt>${escapeHtml(label)}</dt>
      <dd>
        <span class="opportunity-card__bar" aria-hidden="true"><span style="width:${width}%"></span></span>
        <span class="opportunity-card__factor-value">${percent === null ? '—' : `${percent}`}</span>
      </dd>
    </div>
  `;
}

function aggregateFactors(item) {
  return {
    sourceHeat: finiteOrNull(item.sourceSummary?.heat),
    targetCold: averageFactor(item.targetScores, 'targetCold'),
    localizationGap: averageFactor(item.targetScores, 'localizationGap'),
    substituteWeakness: averageFactor(item.targetScores, 'substituteWeakness'),
  };
}

function averageFactor(targetScores, key) {
  const values = (targetScores ?? [])
    .map((target) => finiteOrNull(target?.factors?.[key]))
    .filter((value) => value !== null);
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function renderStatusBlock(state, options) {
  const generated = state.generatedAt
    ? `<span>${escapeHtml(t('opportunityRadar.generatedAt', { date: String(state.generatedAt).slice(0, 10) }))}</span>`
    : '';
  const nextRefresh = state.nextAllowedAt
    ? `<span>${escapeHtml(t('opportunityRadar.nextAllowedAt', { time: formatUtcTime(state.nextAllowedAt) }))}</span>`
    : '';
  if (state.kind === 'loading') {
    return `<div class="opportunity-radar__notice opportunity-radar__notice--loading">${escapeHtml(t('opportunityRadar.loading'))}</div>`;
  }
  if (state.kind === 'refreshing') {
    return `<div class="opportunity-radar__notice opportunity-radar__notice--loading">${escapeHtml(t('opportunityRadar.refreshing'))}${generated}</div>`;
  }
  if (state.kind === 'queued') {
    return `<div class="opportunity-radar__notice">${escapeHtml(t('opportunityRadar.queued'))}${nextRefresh}${retryButton(options)}</div>`;
  }
  if (state.kind === 'ready') {
    return `<div class="opportunity-radar__notice">${escapeHtml(t('opportunityRadar.cached'))}${generated}</div>`;
  }
  if (state.kind === 'limited') {
    return `<div class="opportunity-radar__notice opportunity-radar__notice--warn">${escapeHtml(t('opportunityRadar.limited'))}${nextRefresh}${retryButton(options)}</div>`;
  }
  if (state.kind === 'paused') {
    return `<div class="opportunity-radar__notice opportunity-radar__notice--warn">${escapeHtml(t('opportunityRadar.paused'))}${retryButton(options)}</div>`;
  }
  if (state.kind === 'error') {
    return `<div class="opportunity-radar__notice opportunity-radar__notice--error">${escapeHtml(t('opportunityRadar.error', { message: state.message ?? 'unknown' }))}${retryButton(options)}</div>`;
  }
  return `<div class="opportunity-radar__notice opportunity-radar__notice--empty">${escapeHtml(t('opportunityRadar.empty'))}${retryButton(options)}</div>`;
}

function renderControls(state, options) {
  if (typeof options.onScan !== 'function') return '';
  const hasCards = (state.opportunities ?? []).length > 0 || (state.chartCandidates ?? []).length > 0;
  const busy = state.kind === 'loading' || state.kind === 'refreshing' || state.kind === 'queued';
  const capped = (state.kind === 'limited' || state.kind === 'paused') && (state.retryAfter || state.nextAllowedAt);
  const disabled = busy || capped;
  const label = state.kind === 'refreshing'
    ? t('opportunityRadar.scanning')
    : hasCards
      ? t('opportunityRadar.refresh')
      : t('opportunityRadar.scanNow');
  return `
    <div class="opportunity-radar__actions">
      <button type="button" class="btn opportunity-radar__scan" data-opportunity-scan${disabled ? ' disabled' : ''}>
        ${escapeHtml(label)}
      </button>
    </div>
  `;
}

function retryButton(options) {
  return typeof options.onRetry === 'function'
    ? ` <button type="button" class="btn btn--sm btn--ghost" data-opportunity-retry>${escapeHtml(t('opportunityRadar.retry'))}</button>`
    : '';
}

function keepPreviousOpportunities(state, previousState) {
  const previousOpportunities = Array.isArray(previousState?.opportunities) ? previousState.opportunities : [];
  const previousChartCandidates = Array.isArray(previousState?.chartCandidates) ? previousState.chartCandidates : [];
  if ((state.opportunities ?? []).length > 0 || (state.chartCandidates ?? []).length > 0) {
    return state;
  }
  if (previousOpportunities.length === 0 && previousChartCandidates.length === 0) return state;
  if (!['refreshing', 'queued', 'limited', 'paused', 'error'].includes(state.kind)) {
    return state;
  }
  return {
    ...state,
    generatedAt: state.generatedAt ?? previousState.generatedAt ?? null,
    report: state.report ?? previousState.report ?? null,
    opportunities: previousOpportunities,
    chartArtifact: state.chartArtifact ?? previousState.chartArtifact ?? null,
    chartCandidates: previousChartCandidates,
    baselineStatus: state.baselineStatus ?? previousState.baselineStatus ?? null,
    baselineDate: state.baselineDate ?? previousState.baselineDate ?? null,
    methodology: state.methodology ?? previousState.methodology ?? null,
  };
}

function formatUtcTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

function reasonText(reason) {
  if (!reason) return t('opportunityRadar.noReasons');
  const market = reason.market ? countryName(reason.market, String(reason.market).toUpperCase()) : '';
  const key = `opportunityRadar.reason.${reason.code}`;
  const translated = t(key, { market });
  return translated === key ? (reason.detail ?? reason.code) : translated;
}

function kindLabel(kind) {
  if (kind === 'hot-new-entry') return t('opportunityRadar.kind.hotNewEntry');
  if (kind === 'fast-climber') return t('opportunityRadar.kind.fastClimber');
  if (kind === 'steady-climber') return t('opportunityRadar.kind.steadyClimber');
  if (kind === 'multi-window-climber') return t('opportunityRadar.kind.multiWindowClimber');
  return t('opportunityRadar.kind.newEntry');
}

function chartTypeLabel(chartType) {
  if (chartType === 'top-free') return t('opportunityRadar.chartType.topFree');
  if (chartType === 'top-paid') return t('opportunityRadar.chartType.topPaid');
  if (chartType === 'top-grossing') return t('opportunityRadar.chartType.topGrossing');
  return chartType ? String(chartType) : t('opportunityRadar.unknownChartType');
}

function rankMovementText(candidate) {
  const current = candidate.currentRank === null ? '?' : `#${candidate.currentRank}`;
  if (candidate.previousRank === null) {
    return t('opportunityRadar.rank.newTo', { current });
  }
  const previous = `#${candidate.previousRank}`;
  const gain = candidate.rankGain === null ? '' : ` +${candidate.rankGain}`;
  return t('opportunityRadar.rank.moved', { previous, current, gain });
}

function reviewHintText(candidate) {
  if (candidate.filterStatus === 'filtered') {
    return t('opportunityRadar.reviewHint.filtered');
  }
  if (candidate.filterStatus === 'downranked') {
    return t('opportunityRadar.reviewHint.downranked');
  }
  if (candidate.filterStatus === 'needs_review') {
    return t('opportunityRadar.reviewHint.needsReview');
  }
  if (candidate.baselineStatus !== 'complete') {
    return t('opportunityRadar.reviewHint.baseline');
  }
  if (candidate.kind === 'steady-climber' || candidate.kind === 'multi-window-climber') {
    return t('opportunityRadar.reviewHint.steadyClimber');
  }
  if (candidate.reviewHints.includes('lookup-enrichment-missing-or-low-rating-count')) {
    return t('opportunityRadar.reviewHint.lookup');
  }
  if (candidate.kind === 'fast-climber') {
    return t('opportunityRadar.reviewHint.fastClimber');
  }
  return t('opportunityRadar.reviewHint.ready');
}

function filterStatus(value) {
  if (value === 'downranked' || value === 'filtered' || value === 'needs_review') return value;
  return 'candidate';
}

function filterStatusLabel(status) {
  const key = `opportunityRadar.filterStatus.${status}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

function filterCategoryLabel(category) {
  const key = `opportunityRadar.filterCategory.${category}`;
  const translated = t(key);
  return translated === key ? category : translated;
}

function filterReasonText(reason) {
  const key = `opportunityRadar.filterReason.${reason}`;
  const translated = t(key);
  return translated === key ? reason : translated;
}

function accentTitleSegment(text, segment) {
  const source = String(text ?? '');
  const needle = String(segment ?? '').trim();
  if (!source || !needle) return escapeHtml(source);
  const index = source.toLocaleLowerCase().indexOf(needle.toLocaleLowerCase());
  if (index === -1) return escapeHtml(source);
  const before = source.slice(0, index);
  const match = source.slice(index, index + needle.length);
  const after = source.slice(index + needle.length);
  return `${escapeHtml(before)}<span class="title-accent">${escapeHtml(match)}</span>${escapeHtml(after)}`;
}

function integerOrNull(value) {
  return Number.isInteger(value) ? value : null;
}

function finiteOrNull(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      default: return '&#39;';
    }
  });
}

function escapeAttr(value) {
  return escapeHtml(value);
}
